import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query'
import { activityFeedQueryKeys } from '../activity-feed.query'
import {
  deleteCustomerReview,
  getCustomerReview,
  getCustomerReviews,
  hideCustomerReview,
  publishCustomerReview,
  rejectCustomerReview,
  updateCustomerReview,
  type CustomerReviewIndexQueryKeyEntries,
  type CustomerReviewScope,
  type CustomerReviewShowKeyEntries,
} from './customer-reviews.api'
import type {
  CustomerReviewIdInput,
  CustomerReviewIndexFilters,
  CustomerReviewIndexResponse,
  UpdateCustomerReviewInput,
} from './customer-reviews.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface CustomerReviewIndexScope extends OrganizationScope {
  filters?: CustomerReviewIndexFilters | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type CustomerReviewIndexQueryKey = [AdminQueryRoot & CustomerReviewIndexQueryKeyEntries]
type CustomerReviewShowQueryKey = [AdminQueryRoot & CustomerReviewShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const customerReviewQueryKeys = {
  all: [{ entity: 'customer-review' }] satisfies AdminQueryKey,
  index: ({
    organizationId,
    filters,
    page,
    pageSize,
  }: CustomerReviewIndexScope = {}): CustomerReviewIndexQueryKey =>
    buildScopedAdminQueryKey(customerReviewQueryKeys.all[0], {
      organizationId,
      scope: 'index',
      filters,
      page,
      pageSize,
    }),
  show: ({ organizationId, customerReviewId }: CustomerReviewScope): CustomerReviewShowQueryKey =>
    buildScopedAdminQueryKey(customerReviewQueryKeys.all[0], {
      organizationId,
      scope: 'show',
      customerReviewId: Number(customerReviewId),
    }),
}

export const customerReviewQueries = {
  index: ({ organizationId, filters, page, pageSize }: CustomerReviewIndexScope = {}) =>
    indexTableQueryOptions({
      queryKey: customerReviewQueryKeys.index({ organizationId, filters, page, pageSize }),
      queryFn: getCustomerReviews,
    }),
  show: ({ organizationId, customerReviewId }: CustomerReviewScope) =>
    queryOptions({
      queryKey: customerReviewQueryKeys.show({ organizationId, customerReviewId }),
      queryFn: getCustomerReview,
      placeholderData: () => {
        const cachedIndexQueries = queryClient.getQueriesData<CustomerReviewIndexResponse>({
          queryKey: customerReviewQueryKeys.index({ organizationId }),
        })

        for (const [, response] of cachedIndexQueries) {
          const cachedReview = response?.data.find(
            (review) => review.id === Number(customerReviewId)
          )

          if (cachedReview) {
            return cachedReview
          }
        }

        return undefined
      },
    }),
}

export function useCustomerReviews({
  organizationId,
  filters,
  page,
  pageSize,
  ...params
}: CustomerReviewIndexScope & QueryParams<ReturnType<typeof customerReviewQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...customerReviewQueries.index({
      organizationId: resolvedOrganizationId,
      filters,
      page,
      pageSize,
    }),
    ...params,
  })
}

export function useCustomerReview(
  customerReviewId: CustomerReviewIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof customerReviewQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...customerReviewQueries.show({
      organizationId: resolvedOrganizationId,
      customerReviewId,
    }),
    ...params,
  })
}

function useCustomerReviewActionMutation<TResult>(
  action: (scope: CustomerReviewScope) => Promise<TResult>
) {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (customerReviewId: CustomerReviewIdInput) =>
      action({ organizationId, customerReviewId }),
    onSuccess: () => invalidateCustomerReviewMutationQueries(queryClient, organizationId),
  })
}

export function useUpdateCustomerReviewMutation() {
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateCustomerReviewInput & { customerReviewId: CustomerReviewIdInput }) =>
      updateCustomerReview({ organizationId, ...values }),
    onSuccess: () => invalidateCustomerReviewMutationQueries(queryClient, organizationId),
  })
}

function invalidateCustomerReviewMutationQueries(queryClient: QueryClient, organizationId: number) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: customerReviewQueryKeys.all }),
    queryClient.invalidateQueries({
      queryKey: activityFeedQueryKeys.all({ organizationId }),
    }),
  ])
}

export function usePublishCustomerReviewMutation() {
  return useCustomerReviewActionMutation(publishCustomerReview)
}

export function useHideCustomerReviewMutation() {
  return useCustomerReviewActionMutation(hideCustomerReview)
}

export function useRejectCustomerReviewMutation() {
  return useCustomerReviewActionMutation(rejectCustomerReview)
}

export function useDeleteCustomerReviewMutation() {
  return useCustomerReviewActionMutation(deleteCustomerReview)
}
